import React, { useEffect, useMemo, useState } from 'react';

import { GQL } from '@aline/common';
import { params } from '@aline/routes';
import { Alert, Button, Collapse, Fade, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import type { LucideIcon } from 'lucide-react';
import { matchSorter } from 'match-sorter';
import { Helmet } from 'react-helmet';
import { useLocalStorage } from 'react-use';
import type { StatusBadgeStatus } from 'src/components/admin/integrations/StatusBadge';
import { StatusBadge, StatusIcon } from 'src/components/admin/integrations/StatusBadge';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import { SearchInput } from 'src/components/SearchInput';
import { CrmVaultAction } from 'src/components/send-for-signature/components/AddIntegration';
import { Apollo } from 'src/gql/generated';
import { useConsumePendingParam } from 'src/helpers/routes';
import {
  getIntegrationConnectionNote,
  getIsIntegrationReady,
  useAlineCrmSession,
  useFileStorageConnections,
  useOnFolderSyncJobUpdateSubscription,
  useUserIntegrations,
} from 'src/hooks';
import { useInboxNotifications } from 'src/hooks/inbox-notifications';
import { CrmIntegrationIcon } from 'src/images/integrations/CrmIntegrationIcon';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';

import { ConnectorsContent } from './agent-personalization/connectors-content';
import {
  ChannelInstallBanner,
  ChannelIntegrationRow,
  integrationCardStyles,
  integrationIconStyles,
  integrationNameStyles,
  IntegrationRow,
  IntegrationRowSkeleton,
} from './integrations';
import {
  IntegrationActionButton,
  integrationHoverActionRevealStyles,
} from './integrations/IntegrationActionButton';
import {
  INTEGRATIONS_NAV_SECTIONS,
  INTEGRATIONS_SECTION,
} from './integrations/integrations-nav-sections';
import {
  useIntegrationsScrollRoot,
  useIntegrationsSectionNav,
  useIntegrationsSectionRef,
} from './integrations/integrations-section-spy';

const IntegrationsSectionIdSchema = z.enum(INTEGRATIONS_SECTION);

const FILTER_FADE_MS = 180;
const FILE_STORAGE_SKELETON_COUNT = 5;

/**
 * `isConnected` is remembered so the "Connected" line under a provider's name can hold its
 * height while the live state loads, instead of appearing afterwards and pushing the page down.
 */
type FileStorageStructureRow = Pick<FileStorageConnectionRow, 'imageUrl' | 'name' | 'serviceId'> & {
  isConnected: boolean;
};

// Built lazily: tests mock the generated module, so the enum is not available at import time.
function readFileStorageStructure(raw: string): FileStorageStructureRow[] {
  return z
    .array(
      z.object({
        imageUrl: z.string(),
        isConnected: z.boolean().default(false),
        name: z.string(),
        serviceId: z.nativeEnum(Apollo.FileStorageIntegration),
      })
    )
    .catch([])
    .parse(JSON.parse(raw));
}
const CONNECTED_STATUS_COLLAPSE_MS = 180;

// The logo badge; a disconnected or loading card has none.
const CRM_CARD_ICON_STATUS = {
  attention: 'warning',
  connected: 'success',
  disconnected: 'neutral',
  loading: 'neutral',
  unavailable: 'neutral',
} satisfies Record<CrmCardStatus, StatusBadgeStatus>;

const SHOWS_STATUS_LINE: Readonly<Record<CrmCardStatus, boolean>> = {
  attention: true,
  connected: true,
  disconnected: false,
  loading: false,
  unavailable: true,
};

const FETCH_POLICY = {
  fetchPolicy: 'cache-and-network' as const,
  nextFetchPolicy: 'cache-first' as const,
};

const truncate = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} satisfies React.CSSProperties;

const CRMS = (
  [
    [Apollo.Integration.Act, 'Act!'],
    [Apollo.Integration.ActiveCampaign, 'ActiveCampaign'],
    [Apollo.Integration.Close, 'Close'],
    [Apollo.Integration.Copper, 'Copper'],
    [Apollo.Integration.HubSpot, 'HubSpot'],
    [Apollo.Integration.MicrosoftDynamics, 'Microsoft Dynamics'],
    [Apollo.Integration.Pipedrive, 'Pipedrive'],
    [Apollo.Integration.Salesflare, 'Salesflare'],
    [Apollo.Integration.SalesForce, 'Salesforce'],
    [Apollo.Integration.Teamleader, 'Teamleader'],
    [Apollo.Integration.ZendeskSell, 'Zendesk Sell'],
    [Apollo.Integration.ZohoCrm, 'Zoho CRM'],
  ] as const
).map(([integration, name]) => ({ integration, name }));

const HRIS_SYSTEMS = (
  [
    [Apollo.Integration.Gusto, 'Gusto'],
    [Apollo.Integration.HiBob, 'HiBob'],
    [Apollo.Integration.Rippling, 'Rippling'],
    [Apollo.Integration.SageHr, 'Sage HR'],
    [Apollo.Integration.Workday, 'Workday'],
  ] as const
).map(([integration, name]) => ({ integration, name }));

const useStyles = makeStyles()(theme => ({
  cardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  crmCard: {
    ...integrationCardStyles(theme),
    ...integrationHoverActionRevealStyles,
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1.5),
    minWidth: 0,
    padding: theme.spacing(1.5),
  },
  crmCheckMark: integrationIconStyles(theme).checkMark,
  crmGrid: {
    '@container (min-width: 900px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    '@container (min-width: 1300px)': {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
    display: 'grid',
    gap: theme.spacing(1.5),
    gridTemplateColumns: 'minmax(0, 1fr)',
    width: '100%',
  },
  crmIcon: integrationIconStyles(theme).icon,
  crmIconBox: integrationIconStyles(theme).iconBox,
  crmName: {
    ...truncate,
    ...integrationNameStyles,
  },
  fadeSection: {
    width: '100%',
  },
  listContainer: {
    boxSizing: 'border-box',
    containerType: 'inline-size',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(3),
    maxWidth: 1920,
    padding: 16,
    width: '100%',
  },
  noResults: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(4, 0),
    textAlign: 'center',
  },
  searchWrapper: {
    maxWidth: 320,
    width: '100%',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    scrollMarginTop: theme.spacing(2),
  },
  sectionHeader: {
    alignItems: 'center',
    color: '#424B54',
    display: 'flex',
    fontFamily: '"Open Sans", sans-serif',
    fontSize: 24,
    fontWeight: 700,
    gap: theme.spacing(1),
    lineHeight: 'normal',
  },
}));

export function IntegrationsList(): JSX.Element {
  const { classes } = useStyles();
  const [search, setSearch] = useState('');
  const { isSpyActive, listRef, scrollRoot } = useIntegrationsScrollRoot();
  const { scrollToSection, selectSection } = useIntegrationsSectionNav();
  const [sectionArrival, acknowledgeSectionArrival] = useConsumePendingParam(
    params.integrationsSection
  );
  const {
    error: fileStorageError,
    fileStorageConnections,
    initialLoading: fileStorageLoading,
    refetch: refetchFileStorage,
  } = useFileStorageConnections(FETCH_POLICY);
  /**
   * Which providers exist (names, logos) rarely changes, so the last known list is kept
   * across reloads and drawn immediately while the live connection states load.
   */
  const [fileStorageStructure = [], setFileStorageStructure] = useLocalStorage(
    'integrations.fileStorageStructure',
    [],
    {
      deserializer: readFileStorageStructure,
      raw: false,
      serializer: JSON.stringify,
    }
  );

  useEffect(() => {
    // A failed fetch must not overwrite the last known structure with an empty list.
    if (fileStorageLoading || fileStorageError) return;
    setFileStorageStructure(
      fileStorageConnections.map(({ imageUrl, name, serviceId, state }) => ({
        imageUrl,
        isConnected: state === Apollo.ApiDeckConnectionState.Callable,
        name,
        serviceId,
      }))
    );
  }, [fileStorageConnections, fileStorageError, fileStorageLoading, setFileStorageStructure]);
  const {
    allConnections: crmConnections,
    hrisConnections,
    initialLoading: crmLoading,
  } = useUserIntegrations(FETCH_POLICY);
  const { canConnectCrm } = useAlineCrmSession();
  const { items: inboxItems } = useInboxNotifications();
  // A CRM with an open connection notification reads "Needs attention" on its card.
  const integrationsWithIssues = useMemo(
    () =>
      new Set(
        inboxItems.flatMap(item =>
          item.parsed.kind === GQL.InboxNotificationKind.CrmConnectionIssue
            ? [item.parsed.payload.integration]
            : []
        )
      ),
    [inboxItems]
  );
  useOnFolderSyncJobUpdateSubscription();

  useEffect(() => {
    if (!sectionArrival) return;
    const section = IntegrationsSectionIdSchema.safeParse(sectionArrival.value);

    /**
     * Scrolling starts right away, on skeletons if that is all there is. The section stays
     * selected while the cards above it grow into their real height, and the spy scrolls back
     * to it as they do, so waiting for the connection states would only add a stall.
     */
    if (section.success) {
      selectSection(section.data);
      scrollToSection(section.data);
    }

    acknowledgeSectionArrival();
  }, [acknowledgeSectionArrival, scrollToSection, sectionArrival, selectSection]);

  const query = search.trim();
  const channelsNav = getIntegrationsNavSection(INTEGRATIONS_SECTION.channels);
  const fileStorageNav = getIntegrationsNavSection(INTEGRATIONS_SECTION.fileStorage);
  const connectorsNav = getIntegrationsNavSection(INTEGRATIONS_SECTION.agentConnectors);
  const {
    data: toolkitData,
    error: toolkitError,
    loading: toolkitLoading,
  } = Apollo.useGetExternalToolkitsQuery(FETCH_POLICY);
  const toolkitInitialLoading = !toolkitData && toolkitLoading;
  const toolkits = toolkitData?.externalToolkits ?? [];
  const filteredToolkits = filterSection(toolkits, query, connectorsNav.searchTerms, [
    'name',
    'slug',
  ]);
  const showConnectors =
    matchesSection(query, connectorsNav.searchTerms) ||
    filteredToolkits.length > 0 ||
    toolkitInitialLoading ||
    (!toolkitData && Boolean(toolkitError));
  const crmsNav = getIntegrationsNavSection(INTEGRATIONS_SECTION.crms);
  const hrisNav = getIntegrationsNavSection(INTEGRATIONS_SECTION.hris);

  const showChannels = useMemo(
    () => matchesSection(query, channelsNav.searchTerms),
    [channelsNav.searchTerms, query]
  );

  const filteredFileStorage = useMemo(
    () => filterSection(fileStorageConnections, query, fileStorageNav.searchTerms, ['name']),
    [fileStorageConnections, fileStorageNav.searchTerms, query]
  );

  const filteredFileStorageStructure = useMemo(
    () => filterSection(fileStorageStructure, query, fileStorageNav.searchTerms, ['name']),
    [fileStorageNav.searchTerms, fileStorageStructure, query]
  );

  const filteredCrms = useMemo(
    () => filterSection(CRMS, query, crmsNav.searchTerms, ['name']),
    [crmsNav.searchTerms, query]
  );

  const showFileStorage =
    filteredFileStorage.length > 0 ||
    (fileStorageLoading && matchesSection(query, fileStorageNav.searchTerms));
  const showCrms = filteredCrms.length > 0;
  const filteredHris = useMemo(
    () => filterSection(HRIS_SYSTEMS, query, hrisNav.searchTerms, ['name']),
    [hrisNav.searchTerms, query]
  );
  const showHris = filteredHris.length > 0;
  const hasAnyResults = showChannels || showFileStorage || showCrms || showHris || showConnectors;
  const crmConnectionByIntegration = useMemo(
    () => new Map(crmConnections.map(connection => [connection.integration, connection])),
    [crmConnections]
  );

  return (
    <>
      <Helmet>
        <title>Integrations</title>
      </Helmet>
      <div ref={listRef} className={classes.listContainer}>
        <ChannelInstallBanner />
        <div className={classes.searchWrapper}>
          <SearchInput
            autoFocus={false}
            placeholder="Search integrations..."
            search={search}
            setSearch={setSearch}
          />
        </div>
        <Fade unmountOnExit in={showChannels} timeout={FILTER_FADE_MS}>
          <div className={classes.fadeSection}>
            <Section
              enabled={isSpyActive}
              icon={channelsNav.icon}
              id={channelsNav.id}
              label={channelsNav.label}
              root={scrollRoot}
            >
              <div className={classes.cardList}>
                <ChannelIntegrationRow provider={Apollo.ChannelProvider.Slack} />
                <ChannelIntegrationRow provider={Apollo.ChannelProvider.Teams} />
              </div>
            </Section>
          </div>
        </Fade>
        <Fade unmountOnExit in={showFileStorage} timeout={FILTER_FADE_MS}>
          <div className={classes.fadeSection}>
            <Section
              enabled={isSpyActive}
              icon={fileStorageNav.icon}
              id={fileStorageNav.id}
              label={fileStorageNav.label}
              root={scrollRoot}
            >
              <FileStorageCards
                connections={filteredFileStorage}
                loadFailed={Boolean(fileStorageError) && !fileStorageConnections.length}
                loading={fileStorageLoading}
                structure={filteredFileStorageStructure}
                onRetry={refetchFileStorage}
              />
            </Section>
          </div>
        </Fade>
        {[
          { items: filteredCrms, nav: crmsNav, show: showCrms },
          { items: filteredHris, nav: hrisNav, show: showHris },
        ].map(({ items, nav, show }) => (
          <Fade key={nav.id} unmountOnExit in={show} timeout={FILTER_FADE_MS}>
            <div className={classes.fadeSection}>
              <Section
                enabled={isSpyActive}
                icon={nav.icon}
                id={nav.id}
                label={nav.label}
                root={scrollRoot}
              >
                <div className={classes.crmGrid}>
                  {items.map(({ integration, name }) => {
                    const connection = crmConnectionByIntegration.get(integration);
                    const state = connection?.state ?? null;
                    const isConnected = state === Apollo.ApiDeckConnectionState.Callable;
                    const status = getCrmCardStatus({
                      isConnected,
                      isUnavailable: !getIsIntegrationReady({
                        hrisConnections,
                        initialLoading: crmLoading,
                        integration,
                      }),
                      loading: crmLoading && state === null,
                      needsAttention: integrationsWithIssues.has(integration),
                    });

                    return (
                      <Paper key={integration} className={classes.crmCard} elevation={0}>
                        <Stack
                          direction="row"
                          sx={{
                            alignItems: 'center',
                            flex: 1,
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <IntegrationConnectionTooltip
                            enabled={canConnectCrm}
                            integration={integration}
                          >
                            <div className={classes.crmIconBox}>
                              <CrmIntegrationIcon
                                className={classes.crmIcon}
                                integration={integration}
                              />
                              <StatusIcon
                                className={classes.crmCheckMark}
                                status={CRM_CARD_ICON_STATUS[status]}
                              />
                            </div>
                          </IntegrationConnectionTooltip>
                          <Stack sx={{ flex: 1, minWidth: 0 }}>
                            <span className={classes.crmName}>{name}</span>
                            <Collapse
                              unmountOnExit
                              in={SHOWS_STATUS_LINE[status]}
                              timeout={CONNECTED_STATUS_COLLAPSE_MS}
                            >
                              <CrmCardStatusBadge status={status} />
                            </Collapse>
                          </Stack>
                        </Stack>
                        <Stack direction="row" sx={{ alignItems: 'center', flexShrink: 0, gap: 1 }}>
                          {status === 'loading' ? (
                            <Skeleton height={20} variant="text" width={60} />
                          ) : (
                            <CrmVaultAction integration={integration}>
                              <CrmVaultAction.Control tooltip={false}>
                                {({ disabled, muted, open }) => (
                                  <IntegrationActionButton
                                    disabled={disabled}
                                    muted={muted}
                                    onClick={open}
                                  >
                                    {isConnected ? 'Manage' : 'Connect'}
                                  </IntegrationActionButton>
                                )}
                              </CrmVaultAction.Control>
                            </CrmVaultAction>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </div>
              </Section>
            </div>
          </Fade>
        ))}
        <Fade unmountOnExit in={showConnectors} timeout={FILTER_FADE_MS}>
          <div className={classes.fadeSection}>
            <Section
              enabled={isSpyActive}
              icon={connectorsNav.icon}
              id={connectorsNav.id}
              label={connectorsNav.label}
              root={scrollRoot}
            >
              <ConnectorsContent
                failed={!toolkits.length && Boolean(toolkitError)}
                loading={toolkitInitialLoading}
                toolkits={filteredToolkits}
              />
            </Section>
          </div>
        </Fade>
        <Fade unmountOnExit in={!hasAnyResults} timeout={FILTER_FADE_MS}>
          <Typography className={classes.noResults} variant="body2">
            No integrations match your search
          </Typography>
        </Fade>
      </div>
    </>
  );
}

function getIntegrationsNavSection(
  id: (typeof INTEGRATIONS_SECTION)[keyof typeof INTEGRATIONS_SECTION]
) {
  const section = INTEGRATIONS_NAV_SECTIONS.find(item => item.id === id);
  if (!section) throw new Error(`Unknown integrations nav section: ${id}`);
  return section;
}

function matchesSection(query: string, searchTerms: readonly string[]) {
  return !query || matchSorter([...searchTerms], query).length > 0;
}

function filterSection<T>(
  items: readonly T[],
  query: string,
  searchTerms: readonly string[],
  keys: (keyof T & string)[]
) {
  if (matchesSection(query, searchTerms)) return [...items];
  return matchSorter([...items], query, { keys });
}

type FileStorageConnectionRow = ReturnType<
  typeof useFileStorageConnections
>['fileStorageConnections'][number];

function FileStorageCards({
  connections,
  loadFailed,
  loading,
  onRetry,
  structure,
}: {
  connections: FileStorageConnectionRow[];
  loadFailed: boolean;
  loading: boolean;
  onRetry: () => Promise<unknown>;
  structure: FileStorageStructureRow[];
}) {
  const { classes } = useStyles();
  const rows = loading
    ? structure.map(row => ({
        ...row,
        state: null,
        statusLoading: row.isConnected,
      }))
    : connections.map(row => ({ ...row, statusLoading: false }));
  if (loading && rows.length === 0)
    return (
      <div className={classes.cardList}>
        {Array.from({ length: FILE_STORAGE_SKELETON_COUNT }).map((_, index) => (
          <IntegrationRowSkeleton key={`skeleton-${index}`} />
        ))}
      </div>
    );
  /**
   * With nothing loaded the cached providers cannot be shown either: their connection state is
   * unknown, so a "Connect" button on a connected provider would be a lie.
   */
  if (loadFailed)
    return (
      <Alert
        action={
          <Button color="inherit" size="small" onClick={() => void onRetry()}>
            Retry
          </Button>
        }
        severity="error"
      >
        File storage connections could not be loaded.
      </Alert>
    );
  if (rows.length === 0)
    return (
      <Typography color="text.secondary" variant="body2">
        No file storage providers are available for this account.
      </Typography>
    );

  return (
    <div className={classes.cardList}>
      {rows.map(row => (
        <IntegrationRow
          key={row.serviceId}
          connections={connections}
          imageUrl={row.imageUrl}
          integration={row.serviceId}
          name={row.name}
          state={row.state}
          stateLoading={loading}
          statusLoading={row.statusLoading}
        />
      ))}
    </div>
  );
}

function Section({
  children,
  className,
  enabled,
  icon: Icon,
  id,
  label,
  root,
}: {
  children: React.ReactNode;
  enabled: boolean;
  icon: LucideIcon;
  id: string;
  label: string;
  root: HTMLElement | null;
  className?: string;
}) {
  const { classes, cx } = useStyles();
  const sectionRef = useIntegrationsSectionRef({ enabled, id, root });

  return (
    <section ref={sectionRef} className={cx(classes.section, className)} id={id}>
      <Typography className={classes.sectionHeader} variant="subtitle1">
        <Icon size={22} strokeWidth={1.75} />
        {label}
      </Typography>
      {children}
    </section>
  );
}

type CrmCardStatus = 'attention' | 'connected' | 'disconnected' | 'loading' | 'unavailable';

function getCrmCardStatus(options: {
  readonly isConnected: boolean;
  readonly isUnavailable: boolean;
  readonly loading: boolean;
  readonly needsAttention: boolean;
}): CrmCardStatus {
  if (options.loading) return 'loading';
  if (options.needsAttention) return 'attention';
  if (options.isConnected) return 'connected';
  return options.isUnavailable ? 'unavailable' : 'disconnected';
}

// Anything but an issue reads as connected, so the line keeps its text while it collapses away.
function CrmCardStatusBadge({ status }: { readonly status: CrmCardStatus }): JSX.Element {
  if (status === 'attention')
    return (
      <StatusBadge
        hideIcon
        data-cy="crm-status-needs-attention"
        label="Needs attention"
        status="warning"
      />
    );

  if (status === 'unavailable')
    return (
      <StatusBadge hideIcon data-cy="crm-status-coming-soon" label="Coming soon" status="neutral" />
    );

  return <StatusBadge hideIcon data-cy="crm-status-connected" label="Connected" status="success" />;
}

type ConnectionDetails = Apollo.GetApiDeckConnectionDetailsQuery['apiDeckConnectionDetails'];

type DetailRow = readonly [
  label: string,
  read: (details: NonNullable<ConnectionDetails>) => string | null,
];

const CONNECTION_DETAIL_DATE_FORMAT = 'MMM d, yyyy h:mm a';

const CONNECTION_STATE_LABELS: Readonly<Record<Apollo.ApiDeckConnectionState, string>> = {
  [Apollo.ApiDeckConnectionState.Added]: 'Not connected',
  [Apollo.ApiDeckConnectionState.Authorized]: 'Setup incomplete',
  [Apollo.ApiDeckConnectionState.Available]: 'Not connected',
  [Apollo.ApiDeckConnectionState.Callable]: 'Connected',
  [Apollo.ApiDeckConnectionState.Invalid]: 'Sign-in expired',
};

const CREDENTIALS_MODE_LABELS: Readonly<Record<Apollo.ApiDeckCredentialsMode, string>> = {
  [Apollo.ApiDeckCredentialsMode.CustomerProvided]: 'Customer key',
  [Apollo.ApiDeckCredentialsMode.Development]: 'Dev',
  [Apollo.ApiDeckCredentialsMode.Production]: 'Prod',
};

const CONNECTION_DETAIL_ROWS: readonly DetailRow[] = [
  ['Account', details => details.account],
  ['Account type', details => details.accountType],
  ['Acts as', details => details.crmUserEmail],
  ['Apideck app', details => details.apideckAppId],
  ['Connected', details => formatConnectionDetailDate(details.connectedAt)],
  ['Connected by', details => details.connectedByEmail],
  [
    'Credential type',
    details => (details.credentialsMode ? CREDENTIALS_MODE_LABELS[details.credentialsMode] : null),
  ],
  ['Environment', details => details.environment],
  ['Host', details => details.host],
  ['Last request from CRM', details => formatConnectionDetailDate(details.lastRequestAt)],
  ['Last updated', details => formatConnectionDetailDate(details.updatedAt)],
  ['Status', details => (details.state ? CONNECTION_STATE_LABELS[details.state] : 'Not connected')],
  ['Tenant ID', details => details.tenantId],
];

const useConnectionDetailStyles = makeStyles()(theme => ({
  label: {
    color: theme.palette.grey[400],
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  row: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  value: {
    minWidth: 0,
    overflowWrap: 'anywhere',
    textAlign: 'right',
  },
}));

function IntegrationConnectionTooltip({
  children,
  enabled,
  integration,
}: {
  readonly children: React.ReactElement;
  readonly enabled: boolean;
  readonly integration: Apollo.Integration;
}): JSX.Element {
  const [loadDetails, { data, error, loading }] = Apollo.useGetApiDeckConnectionDetailsLazyQuery({
    fetchPolicy: 'cache-first',
  });
  if (!enabled) return children;

  return (
    <AlineTooltip
      arrow
      disableInteractive={false}
      placement="bottom-start"
      slotProps={{ tooltip: { sx: { maxWidth: 280 } } }}
      title={
        <ConnectionDetailsTitle
          details={data?.apiDeckConnectionDetails}
          error={Boolean(error)}
          loading={loading}
        />
      }
      onOpen={() => void loadDetails({ variables: { integration } })}
    >
      {children}
    </AlineTooltip>
  );
}

function ConnectionDetailsTitle({
  details,
  error,
  loading,
}: {
  readonly details: ConnectionDetails | undefined;
  readonly error: boolean;
  readonly loading: boolean;
}): JSX.Element {
  const { classes } = useConnectionDetailStyles();
  if (loading || details === undefined)
    return error ? <span>Could not load the connection details.</span> : <DetailsSkeleton />;
  if (!details) return <span>No connection details.</span>;

  const rows = CONNECTION_DETAIL_ROWS.flatMap(([label, read]) => {
    const value = read(details);
    return value ? [{ label, value }] : [];
  });
  const note = getIntegrationConnectionNote({ integration: details.integration });

  return (
    <Stack data-cy="integration-connection-details" sx={{ gap: 0.5, minWidth: 220 }}>
      {rows.map(({ label, value }) => (
        <div key={label} className={classes.row}>
          <span className={classes.label}>{label}</span>
          <span className={classes.value}>{value}</span>
        </div>
      ))}
      {Boolean(note) && (
        <div className={classes.row}>
          <span className={classes.label}>Notes</span>
          <span className={classes.value}>{note}</span>
        </div>
      )}
    </Stack>
  );
}

function DetailsSkeleton(): JSX.Element {
  return (
    <Stack sx={{ gap: 0.5, minWidth: 220 }}>
      {[0, 1, 2].map(row => (
        <Skeleton key={row} height={16} variant="text" />
      ))}
    </Stack>
  );
}

function formatConnectionDetailDate(value: string | null): string | null {
  return value ? format(new Date(value), CONNECTION_DETAIL_DATE_FORMAT) : null;
}
